import { ref, unref, watch } from 'vue'
import { v2 } from '@/services/api.js'
import snackbar from '@/js/snackbar.js'

const loading = ref(false)
const fieldLens = ref([])
const boosts = ref([])
const serverError = ref(false)
const errorMessage = ref('')
const affectedArea = ref('')
const typesWithSubtypes = ref([])
const products = ref([])
const allianceManagerBySegment = ref({})

async function fetchRequestTypes() {
  try {
    const { data } = await v2.get('/field-lens/request-types')
    typesWithSubtypes.value = data
  }
  catch(err) {
    console.error(err)
  }
}

async function fetchProducts() {
  try {
    const { data } = await v2.get('/field-lens/products')
    products.value = data
  }
  catch(err) {
    console.error(err)
  }
}

async function fetchFieldLens() {
  loading.value = true
  try {
    const res = await v2.get('/field-lens')
    fieldLens.value = res.data
  }
  catch(err) {
    console.error(err)
    if (err.response?.status == 500) {
      serverError.value = true
      errorMessage.value = err.response.data.message
      affectedArea.value = 'Field Lens'
      return
    }
  }
  finally {
    loading.value = false
  }
}

async function fetchBoosts() {
  loading.value = true
  try {
    const res = await v2.get('/field-lens/boosts')
    boosts.value = res.data
  }
  catch(err) {
    console.error(err)
  }
  finally {
    loading.value = false
  }
}

async function deleteBoost(id) {
  loading.value = true
  try {
    await v2.delete(`/field-lens/${id}/boosts`, id)
    snackbar.show({
      tone: 'success',
      heading: 'Success',
      message: 'Successfully deleted the boost object.',
    })
  }
  catch(err) {
    snackbar.show({
      tone: 'failure',
      heading: 'There was a problem...',
      timeout: 15000,
      message: err.response?.data?.message || 'An error occurred',
    })
  }
  finally {
    loading.value = false
  }
}

async function fetchBoostsByUserID() {
  try {
    loading.value = true
    await v2.put(`/field-lens/${payload.id}`, payload)
  }
  catch(err) {
    console.error(err)
  }
  finally {
    loading.value = false
  }
}


async function update(payload) {
  loading.value = true
  try {
    await v2.put(`/field-lens/${payload.id}`, payload)
    snackbar.show({
      tone: 'success',
      heading: 'Success',
      message: 'Successfully updated the Field Lens request.',
    })
  }
  catch(err) {
    snackbar.show({
      tone: 'failure',
      heading: 'There was a problem...',
      message: err.response?.data?.message || 'An error occurred',
    })
  }
  finally {
    loading.value = false
  }
}

async function create(payload) {
  try {
    const res = await v2.post('/field-lens', payload)
    snackbar.show({
      tone: 'success',
      heading: 'Success',
      message: 'Successfully created a new Field Lens request.',
    })

    return res
  }
  catch(err) {
    snackbar.show({
      tone: 'failure',
      heading: 'There was a problem...',
      timeout: 15000,
      message:err.response?.data?.message || 'An error occurred',
    })
  }
}

// Does this need a snackbar success? If so, we need one for temp drafts existing in frontend as well
async function deleteFieldLens(id) {
  loading.value = true
  try {
    await v2.delete(`/field-lens/${id}`)
    snackbar.show({
      tone: 'success',
      heading: 'Success',
      message: 'Field Lens successfully deleted.',
    })
  }
  catch(err) {
    snackbar.show({
      tone: 'failure',
      heading: 'There was a problem...',
      timeout: 15000,
      message: err.response?.data?.message || 'An error occurred',
    })
  }
  finally {
    loading.value = false
  }
}

async function closeBoosting(id) {
  loading.value = true
  try {
    await v2.patch(`/field-lens/${id}`)
    snackbar.show({
      tone: 'success',
      heading: 'Success',
      message: 'Field Lens boosting successfully closed.',
    })
  }
  catch(err) {
    snackbar.show({
      tone: 'failure',
      heading: 'There was a problem...',
      timeout: 15000,
      message: err.response?.data?.message || 'An error occurred',
    })
  }
  finally {
    loading.value = false
  }
}

async function createBoost(payload, silent = false) {
  loading.value = true
  try {
    await v2.post(`/field-lens/${payload.fieldLensParent}/boosts`, payload)
    // Silent prop serves to hide it when we create both a boost and field lens at the same time
    // So that only the new field lens created snackbar shows
    if (!silent) {
      snackbar.show({
        tone: 'success',
        heading: 'Success',
        message: 'Successfully created a new boosting object.',
      })
    }
  }
  catch(err) {
    snackbar.show({
      tone: 'failure',
      heading: 'There was a problem...',
      timeout: 15000,
      message: err.response?.data?.message || 'An error occurred',
    })
  }
  finally {
    loading.value = false
  }
}

async function updateBoost(payload) {
  try {
    await v2.put(`/field-lens/boosts/${payload.id}`, payload)
    snackbar.show({
      tone: 'success',
      heading: 'Success',
      message: 'Successfully updated the boosting object.',
    })
  }
  catch(err) {
    snackbar.show({
      tone: 'failure',
      heading: 'There was a problem...',
      message: err.response?.data?.message || 'An error occurred',
    })
  }
}

async function logFieldLensExport(payload) {
  try {
    await v2.post('/field-lens/export/log-event', payload)
  }
  catch(err) {
    console.warn('Field Lens export event log failed', err)
  }
}

async function fetchAllianceManagers() {
  try {
    const { data } = await v2.get('/field-lens/alliance-managers')
    allianceManagerBySegment.value = data
  } catch (err) {
    snackbar.show({
      tone: 'failure',
      heading: 'There was a problem...',
      message: err.response?.data?.message || 'An error occurred',
    })
  }
}

// Find the user record whose name exactly matches the Alliance Manager's name
async function findAllianceManagerUser(managerName) {
  const users = (await v2.get('/users/lookup', { params: { name: managerName } })).data
  const match = users.find(user => user.name?.toLowerCase() === managerName.toLowerCase())

  if (!match) {
    return null
  }
  return {
    id: match.id,
    name: match.name,
    email: match.email
  }
}

export default function useFieldLens() {
  return {
    // Variables
    loading,
    fieldLens,
    boosts,
    serverError,
    errorMessage,
    affectedArea,
    typesWithSubtypes,
    products,
    allianceManagerBySegment,
    // Functions
    create,
    fetchFieldLens,
    update,
    createBoost,
    updateBoost,
    closeBoosting,
    fetchBoosts,
    deleteFieldLens,
    deleteBoost,
    logFieldLensExport,
    fetchRequestTypes,
    fetchProducts,
    fetchAllianceManagers,
    findAllianceManagerUser,
  }
}
